import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Flag, Link as LinkIcon, Mail, QrCode, Search } from 'lucide-react';
import Header from '../components/layout/Header';
import VerdictBadge from '../components/analysis/VerdictBadge';
import { api } from '../services/api';

const filters = ['Tout', 'Liens', 'Messages', 'QR codes', 'Signalés'];
const verdictBorder = { danger: 'border-l-verdict-danger', suspect: 'border-l-verdict-suspect', safe: 'border-l-verdict-safe' };
const iconBg = { danger: 'bg-verdict-danger-bg text-verdict-danger', suspect: 'bg-verdict-suspect-bg text-verdict-suspect', safe: 'bg-verdict-safe-bg text-verdict-safe' };

function typeIcon(type, signaled) {
  if (signaled) return Flag;
  if (type === 'email' || type === 'message') return Mail;
  if (type === 'qr') return QrCode;
  return LinkIcon;
}

function HistoryRow({ item, onOpen }) {
  const Icon = typeIcon(item.type, item.signaled);
  return (
    <button type="button" onClick={() => onOpen(item)} className={`w-full text-left bg-white rounded-2xl border border-gray-100 border-l-4 ${verdictBorder[item.verdict]} p-4 flex items-center gap-4 shadow-sm active:scale-[0.99] transition-transform`} aria-label={`Afficher le résultat ${item.label}`}>
      <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.signaled ? 'bg-brand-navy/10 text-brand-navy' : iconBg[item.verdict]}`}>
        <Icon className="w-6 h-6" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          {item.signaled ? <span className="text-xs font-semibold text-brand-navy">SIGNALÉ ·</span> : null}
          <VerdictBadge verdict={item.verdict} size="sm" />
        </span>
        <span className="block font-bold text-text-primary truncate mt-1">{item.label}</span>
        <span className="block text-sm text-text-muted leading-snug">{item.time} · {item.category}</span>
      </span>
      <ChevronRight className="w-5 h-5 text-gray-400" />
    </button>
  );
}

function Section({ title, rows, onOpen }) {
  if (!rows.length) return null;
  return (
    <section className="mx-4 mt-8">
      <h2 className="font-bold text-2xl text-brand-navy mb-4">{title}</h2>
      <div className="space-y-3 md:grid md:grid-cols-2 xl:grid-cols-3 md:gap-3 md:space-y-0">{rows.map((item) => <HistoryRow key={item.id} item={item} onOpen={onOpen} />)}</div>
    </section>
  );
}

export default function HistoryPage() {
  const [active, setActive] = useState('Tout');
  const [query, setQuery] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.getHistory().then((items) => { if (mounted) setAnalyses(items); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => analyses.filter((item) => {
    const matchesFilter = active === 'Tout' || (active === 'Liens' && item.type === 'url') || (active === 'Messages' && (item.type === 'email' || item.type === 'message')) || (active === 'QR codes' && item.type === 'qr') || (active === 'Signalés' && item.signaled);
    const matchesQuery = `${item.label} ${item.category}`.toLowerCase().includes(query.toLowerCase());
    return matchesFilter && matchesQuery;
  }), [active, analyses, query]);

  const openResult = (item) => navigate('/results', { state: { type: item.type, data: item } });

  return (
    <div className="page-container">
      <Header />
      <div className="mx-4 mt-6 relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-text-muted" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} className="w-full h-14 bg-white rounded-2xl pl-12 pr-4 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue/20" placeholder="Rechercher une analyse..." aria-label="Rechercher une analyse" />
      </div>
      <div className="mt-4 overflow-x-auto px-4 [-ms-overflow-style:none] [scrollbar-width:none]" role="tablist" aria-label="Filtres de l'historique">
        <div className="flex gap-3 min-w-max">
          {filters.map((filter) => <button key={filter} type="button" onClick={() => setActive(filter)} aria-pressed={active === filter} className={`min-h-11 px-5 rounded-full font-medium active:scale-95 transition-transform ${active === filter ? 'bg-brand-navy text-white' : 'bg-gray-200 text-text-muted'}`}>{filter}</button>)}
        </div>
      </div>
      {loading ? <p className="mx-4 mt-10 text-center text-text-muted">Chargement de votre historique...</p> : null}
      <Section title="Récent" rows={filtered.filter((item) => item.group === 'recent')} onOpen={openResult} />
      <Section title="Plus tôt cette semaine" rows={filtered.filter((item) => item.group === 'week')} onOpen={openResult} />
      <Section title="Plus ancien" rows={filtered.filter((item) => item.group === 'older')} onOpen={openResult} />
      {!loading && !filtered.length ? <EmptyState onAnalyze={() => navigate('/analyze')} /> : null}
    </div>
  );
}

function EmptyState({ onAnalyze }) {
  return (
    <div className="mx-4 mt-10 card text-center py-8">
      <Search className="w-12 h-12 mx-auto text-text-subtle" />
      <h2 className="mt-4 font-bold text-brand-navy">Aucune analyse trouvée</h2>
      <p className="mt-2 text-sm text-text-muted">Changez de filtre ou lancez une nouvelle vérification.</p>
      <button type="button" onClick={onAnalyze} className="btn-primary mt-5">Analyser maintenant</button>
    </div>
  );
}
